/**
 * Authentication Hook
 *
 * Provides authentication state and methods throughout the app.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi, setTokens, clearTokens, loadStoredRefreshToken, babyApi } from '../services/api';
import {
  generateRSAKeyPair,
  exportPublicKey,
  encryptPrivateKey,
  initializeUserSession,
  loadBabyKeysForSession,
  clearSession,
  isCryptoReady,
} from '../crypto';
import type { User } from '../types';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isInitialized: boolean;
  needsUnlock: boolean; // Session exists but crypto needs password to unlock
  error: string | null;

  // Actions
  register: (email: string, password: string, name: string) => Promise<void>;
  login: (email: string, password: string, twoFactorCode?: string) => Promise<{ requires2FA?: boolean }>;
  logout: () => Promise<void>;
  unlockSession: (password: string) => Promise<void>;
  initialize: () => Promise<void>;
  loadAllBabyKeys: () => Promise<void>;
  clearError: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      isInitialized: false,
      needsUnlock: false,
      error: null,

      register: async (email: string, password: string, name: string) => {
        set({ isLoading: true, error: null });
        try {
          // Generate RSA key pair
          const keyPair = await generateRSAKeyPair();
          const publicKey = await exportPublicKey(keyPair.publicKey);
          const encryptedPrivateKey = await encryptPrivateKey(keyPair.privateKey, password);

          // Register with server
          await authApi.register({
            email,
            password,
            name,
            publicKey,
            encryptedPrivateKey,
          });

          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false, error: (error as Error).message });
          throw error;
        }
      },

      login: async (email: string, password: string, twoFactorCode?: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login({ email, password, twoFactorCode });

          if (response.data?.requires2FA) {
            set({ isLoading: false });
            return { requires2FA: true };
          }

          if (response.data?.user && response.data?.tokens) {
            const { user, tokens } = response.data;

            // Set tokens for API calls
            setTokens(tokens);

            // Initialize crypto session with user's keys
            await initializeUserSession(user.encryptedPrivateKey, password);
            await get().loadAllBabyKeys();

            set({
              user,
              isLoading: false,
              needsUnlock: false,
            });
          }

          return {};
        } catch (error) {
          set({ isLoading: false, error: (error as Error).message });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authApi.logout();
        } finally {
          clearTokens();
          clearSession();
          set({
            user: null,
            isLoading: false,
            needsUnlock: false,
          });
        }
      },

      unlockSession: async (password: string) => {
        set({ isLoading: true, error: null });
        try {
          const user = get().user;
          if (!user || !user.encryptedPrivateKey) {
            throw new Error('No user session to unlock');
          }

          // Re-initialize crypto session with the password
          await initializeUserSession(user.encryptedPrivateKey, password);
          await get().loadAllBabyKeys();

          set({
            isLoading: false,
            needsUnlock: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: 'Incorrect password. Please try again.'
          });
          throw error;
        }
      },

      initialize: async () => {
        // Load stored refresh token
        loadStoredRefreshToken();

        // Only attempt to get user if we have a refresh token
        const hasStoredToken = localStorage.getItem('refreshToken');
        if (!hasStoredToken) {
          set({ isInitialized: true, user: null, needsUnlock: false });
          return;
        }

        // Check if crypto session is still valid (private key in memory)
        if (isCryptoReady()) {
          // Crypto is ready, just validate the token
          try {
            const response = await authApi.getMe();
            if (response.data) {
              set({
                user: response.data,
                isInitialized: true,
                needsUnlock: false,
              });
            } else {
              clearTokens();
              clearSession();
              set({ isInitialized: true, user: null, needsUnlock: false });
            }
          } catch {
            clearTokens();
            clearSession();
            set({ isInitialized: true, user: null, needsUnlock: false });
          }
          return;
        }

        // Crypto session was lost (e.g., page refresh)
        // But we still have a token - fetch user and require unlock
        try {
          const response = await authApi.getMe();
          if (response.data) {
            // User is authenticated but crypto needs unlocking
            set({
              user: response.data,
              isInitialized: true,
              needsUnlock: true, // Show unlock screen instead of login
            });
          } else {
            clearTokens();
            clearSession();
            set({ isInitialized: true, user: null, needsUnlock: false });
          }
        } catch {
          // Token invalid or expired - full logout
          clearTokens();
          clearSession();
          set({ isInitialized: true, user: null, needsUnlock: false });
        }
      },

      clearError: () => set({ error: null }),

      loadAllBabyKeys: async () => {
        set({ isLoading: true });
        try {
          const response = await babyApi.list();
          if (response.data) {
            // Transform response to match expected format for loadBabyKeysForSession
            const babyEncryptedKeys = response.data.map(
              (baby: any) => ({
                babyId: baby.id,
                encryptedBabyKey: baby.encryptedBabyKey,
              })
            );
            await loadBabyKeysForSession(babyEncryptedKeys);
          }
          set({ isLoading: false });
        } catch (error) {
          console.error('Failed to load baby keys:', error);
          set({ isLoading: false, error: (error as Error).message });
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user }),
    }
  )
);

// Hook for checking if user is authenticated
export function useIsAuthenticated(): boolean {
  const user = useAuth((state) => state.user);
  return user !== null;
}

// Hook for checking if session needs unlock
export function useNeedsUnlock(): boolean {
  return useAuth((state) => state.needsUnlock);
}

// Hook for getting current user
export function useCurrentUser(): User | null {
  return useAuth((state) => state.user);
}

// Hook for checking if user is admin
export function useIsAdmin(): boolean {
  const user = useAuth((state) => state.user);
  return user?.isAdmin ?? false;
}
